/* Validates manually typed Order Entry prices against instrument tick increments before submit. */

type PriceValue = number | string | null | undefined;

const tickDivisibilityTolerance = 1e-9;

/** Converts user-entered price or tick text into a finite number while rejecting blanks and malformed values. */
function toFiniteNumber(value: PriceValue): number | undefined {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined;
  }

  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmedValue = value.trim();
  if (!trimmedValue) {
    return undefined;
  }

  const parsedValue = Number(trimmedValue.replaceAll(',', ''));
  return Number.isFinite(parsedValue) ? parsedValue : undefined;
}

/** Distinguishes an omitted price from malformed numeric input for field-specific messages. */
function isBlankPriceValue(value: PriceValue): boolean {
  return value === null
    || value === undefined
    || (typeof value === 'string' && value.trim() === '');
}

/** Confirms the product tick size is usable before it gates manual price submission. */
function toFiniteTickSize(tickSize: PriceValue): number | undefined {
  const parsedTickSize = toFiniteNumber(tickSize);
  return parsedTickSize !== undefined && parsedTickSize > 0
    ? parsedTickSize
    : undefined;
}

/** Returns the number of fixed decimal places needed to display a tick size without trailing zeroes. */
function getDecimalPlaces(value: number): number {
  const valueText = value.toString().toLowerCase();
  const [coefficient, exponentText] = valueText.split('e');
  const coefficientDecimals = coefficient?.split('.')[1]?.length ?? 0;
  const exponent = exponentText === undefined ? 0 : Number(exponentText);

  return Number.isFinite(exponent)
    ? Math.max(0, coefficientDecimals - exponent)
    : coefficientDecimals;
}

/** Formats tick sizes for field-level validation messages without forcing unnecessary trailing zeroes. */
export function formatTickSize(tickSize: PriceValue): string {
  const parsedTickSize = toFiniteTickSize(tickSize);
  if (parsedTickSize === undefined) {
    return '';
  }

  const decimalPlaces = Math.min(getDecimalPlaces(parsedTickSize), 10);
  const formattedTickSize = parsedTickSize.toFixed(decimalPlaces);
  return formattedTickSize.includes('.')
    ? formattedTickSize.replace(/\.?0+$/, '')
    : formattedTickSize;
}

/** Checks a finite price against the instrument tick size using a tolerance for binary float division. */
export function isPriceOnTick(price: number, tickSize: PriceValue): boolean {
  const parsedTickSize = toFiniteTickSize(tickSize);
  if (!Number.isFinite(price) || parsedTickSize === undefined) {
    return false;
  }

  const tickCount = price / parsedTickSize;
  return Math.abs(tickCount - Math.round(tickCount)) <= tickDivisibilityTolerance;
}

/** Builds a label-aware field error when a typed price is not divisible by the selected product tick size. */
export function getPriceTickError(value: PriceValue, tickSize: PriceValue, fieldLabel: string): string | undefined {
  const price = toFiniteNumber(value);
  const parsedTickSize = toFiniteTickSize(tickSize);
  if (price === undefined || parsedTickSize === undefined) {
    return undefined;
  }

  return isPriceOnTick(price, parsedTickSize)
    ? undefined
    : `${fieldLabel} must be in ${formatTickSize(parsedTickSize)} increments.`;
}

/** Preserves the established Limit Price API while sharing label-aware tick arithmetic. */
export function getLimitPriceTickError(value: PriceValue, tickSize: PriceValue): string | undefined {
  return getPriceTickError(value, tickSize, 'Limit price');
}

/** Fails closed when a submitted Limit Price cannot be verified against usable product tick metadata. */
export function getRequiredLimitPriceTickError(value: PriceValue, tickSize: PriceValue): string | undefined {
  if (toFiniteTickSize(tickSize) === undefined) {
    return 'Limit price cannot be validated because product tick size is unavailable.';
  }

  return getLimitPriceTickError(value, tickSize);
}

/** Validates the required, finite, positive portion of the Stop Price rule without product metadata. */
export function getStopPriceValueError(value: PriceValue): string | undefined {
  if (isBlankPriceValue(value)) {
    return 'Stop price is required.';
  }

  const price = toFiniteNumber(value);
  if (price === undefined) {
    return 'Stop price must be a value.';
  }

  if (price <= 0) {
    return 'Stop price must be greater than zero.';
  }

  return undefined;
}

/** Applies the complete product-aware Stop Price rule and fails closed when tick metadata is unusable. */
export function getStopPriceError(value: PriceValue, tickSize: PriceValue): string | undefined {
  const valueError = getStopPriceValueError(value);
  if (valueError) {
    return valueError;
  }

  if (toFiniteTickSize(tickSize) === undefined) {
    return 'Stop price cannot be validated because product tick size is unavailable.';
  }

  return getPriceTickError(value, tickSize, 'Stop price');
}
